import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { api, session, openLeagueSocket } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Flag, Trophy, Users, Calendar, GitBranch, BarChart2, Rss, Settings, Mail, MessageSquare, Crown, AlertTriangle, Zap } from "lucide-react";
import { isDraftEligible } from "@/lib/draftEligibleTemplates";
import TermsModal from "@/components/TermsModal";
import PrivacyModal from "@/components/PrivacyModal";
import AdminFloatingMenu from "@/components/admin/AdminFloatingMenu";
import Takeover from "@/components/onui/Takeover";
import TournamentSwitcher, { getStoredViewTournament, setStoredViewTournament } from "@/components/dashboard/TournamentSwitcher";

import AdvanceTeamsTab from "@/components/admin/AdvanceTeamsTab";
import MarkRoundsTab from "@/components/admin/MarkRoundsTab";
import PlayersTab from "@/components/admin/PlayersTab";
import ScoringTab from "@/components/admin/ScoringTab";
import MatchesTab from "@/components/admin/MatchesTab";
import TeamsTab from "@/components/admin/TeamsTab";
import ScheduleTab from "@/components/admin/ScheduleTab";
import EspnSyncTab from "@/components/admin/EspnSyncTab";
// iter87.174 — Persistent admin entry point for the draft workflow.
import DraftTab from "@/components/admin/DraftTab";import GolfSyncCard from "@/components/dashboard/GolfSyncCard";
// iter45 (M3) — split SettingsTab → LeagueSettingsTab + TournamentSettingsTab
import LeagueSettingsTab from "@/components/admin/LeagueSettingsTab";
import TournamentSettingsTab from "@/components/admin/TournamentSettingsTab";
// iter87.7 — Unified Live Sync tab (one shape, every template).
import LiveSyncTab from "@/components/admin/LiveSyncTab";
// iter87.9 — Danger Zone plane owner.
import TournamentDangerZoneCard from "@/components/admin/TournamentDangerZoneCard";
// iter87.6 — Rivalry Admin tile split: invites + messages + co-admins
// each get their own tile / tab surface.
import RivalryInvitesTab from "@/components/admin/RivalryInvitesTab";
import RivalryMessagesTab from "@/components/admin/RivalryMessagesTab";
import RivalryCoAdminsTab from "@/components/admin/RivalryCoAdminsTab";
// iter87.194 — Trophy Tokens: per-tournament admin token system.
// iter87.554 — also the single home for Squares config now (early
// return inside TrophyTokensTab itself — see that file).
import TrophyTokensTab from "@/components/admin/TrophyTokensTab";
// iter87.199.18 — `?` help popovers on every admin bento tile.
import TileHelpPopover from "@/components/admin/TileHelpPopover";

// iter86.98 — One UI Phase 4: bento tile icons for the admin grid.
const TILE_ICONS = {
    teams: Users, schedule: Calendar, bracket_status: GitBranch,
    scoring: BarChart2, espn: Rss, live_sync: Rss, settings: Settings, players: Users,
    invites: Mail, messages: MessageSquare, co_admins: Crown,
    danger_zone: AlertTriangle, draft: Zap,
    trophy_tokens: Trophy,
};

// iter87.17 — Draft-style Takeover header per admin tile. Every admin
// modal reuses the same TILE_ICONS so opening the tile and its modal
// feels like the same object (tile head → modal head).
const ADMIN_HEAD = {
    teams:          { icon: Users,          tone: "cyber" },
    schedule:       { icon: Calendar,       tone: "cyber" },
    bracket_status: { icon: GitBranch,      tone: "cyber" },
    scoring:        { icon: BarChart2,      tone: "cyber" },
    espn:           { icon: Rss,            tone: "cyber" },
    live_sync:      { icon: Rss,            tone: "cyber" },
    settings:       { icon: Settings,       tone: "neutral" },
    players:        { icon: Users,          tone: "cyber" },
    invites:        { icon: Mail,           tone: "cyber" },
    messages:       { icon: MessageSquare,  tone: "cyber" },
    co_admins:      { icon: Crown,          tone: "cyber" },
    danger_zone:    { icon: AlertTriangle,  tone: "blaze" },
    draft:          { icon: Zap,             tone: "volt" },
    trophy_tokens:  { icon: Trophy,          tone: "volt" },
};

export default function AdminPanel() {
    const { leagueId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState(null);
    // iter45b — scope driven by URL ?scope=tournament|league (default tournament).
    // Sharing/bookmarking either scope is now possible. The AdminMenu in the
    // dashboard header sets the param when navigating here.
    const scope = searchParams.get("scope") === "league" ? "league" : "tournament";
    const setScope = (next) => {
        const params = new URLSearchParams(searchParams);
        params.set("scope", next);
        setSearchParams(params);
    };
    // Tournament being administered — drives which tournament's data we load.
    // iter86.99 — Falls back to the dashboard's stored tournament view so
    // "Admin" opens the tournament you were just looking at (tester flag).
    const [viewTournamentId, setViewTournamentId] = useState(
        () => searchParams.get("tournament_id") || getStoredViewTournament(leagueId) || null
    );
    const [switcherRefreshKey] = useState(0);
    // iter86.98 — One UI Phase 4: the panel lands on the bento grid
    // (tab === null); tapping a tile opens that area in a Takeover.
    // iter87.481 — honor a `?tab=` deep link on first load (e.g. the
    // "Mark complete" nudge linking straight to Danger Zone).
    const [tab, setTab] = useState(() => searchParams.get("tab") || null);
    const [scoring, setScoring] = useState(null);
    // Mark Complete dialog state
    const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
    const [completeConfirmText, setCompleteConfirmText] = useState("");
    const [completing, setCompleting] = useState(false);

    const sessionData = useMemo(() => session.load(), []);

    // iter31n — block AdminPanel until the admin has accepted the current T&C version.
    const [termsBlocking, setTermsBlocking] = useState(false);
    const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
    const [termsAccepting, setTermsAccepting] = useState(false);
    const [currentTermsVersion, setCurrentTermsVersion] = useState(null);

    // iter35a — co-admin role + permissions for the current user
    const [coAdminCtx, setCoAdminCtx] = useState(null);

    const load = useCallback(async () => {
        // iter45b — pass tournament_id so the switcher in this page's header
        // can change which tournament is being administered.
        const d = await api.getLeague(leagueId, { tournamentId: viewTournamentId });
        if (sessionData) {
            const me = d.participants.find(p => p.id === sessionData.participant_id);
            if (!me?.is_admin && !me?.is_co_admin) {
                toast.error("Admins only");
                navigate(`/league/${leagueId}`);
                return;
            }
        }
        setData(d);
        if (d.tournament) {
            setScoring(d.tournament.scoring);
        }
    }, [leagueId, navigate, sessionData, viewTournamentId]);

    useEffect(() => {
        if (!sessionData || sessionData.league_id !== leagueId) {
            navigate("/");
            return;
        }
        // eslint-disable-next-line
        load();
        api.getTermsVersion()
            .then((r) => setCurrentTermsVersion(r?.current_version || "v1"))
            .catch(() => setCurrentTermsVersion("v1"));
    }, [leagueId, load, navigate, sessionData]);

    // iter45b — When URL `?scope=` changes, return to that scope's bento
    // home (iter86.98) so tiles always match the scope being edited.
    // iter87.481 — skip the very first run so a `?tab=` deep link survives
    // the initial mount (this effect used to fire on mount too and wipe it).
    const isFirstScopeEffect = useRef(true);
    useEffect(() => {
        if (isFirstScopeEffect.current) {
            isFirstScopeEffect.current = false;
            return;
        }
        setTab(null);
        // eslint-disable-next-line
    }, [scope]);

    // iter87.481 — `?action=mark_complete` deep link (from the "looks
    // finished" nudge) opens the Mark Complete confirm dialog straight
    // away, then strips the param so a refresh/back doesn't reopen it.
    useEffect(() => {
        if (searchParams.get("action") === "mark_complete") {
            setCompleteDialogOpen(true);
            const params = new URLSearchParams(searchParams);
            params.delete("action");
            setSearchParams(params, { replace: true });
        }
        // eslint-disable-next-line
    }, []);

    // Once we have the admin participant doc + the current version, decide whether
    // to show the blocking T&C modal.
    useEffect(() => {
        if (!data?.participants || !currentTermsVersion || !sessionData) return;
        const me = data.participants.find((p) => p.id === sessionData.participant_id);
        if (!me) return;
        // iter86.77.16 — T&C acceptance is ALWAYS primary-admin-only (see
        // co-admin permission rules). Co-admins must never be blocked by
        // (or able to accept) the terms modal.
        if (!me.is_admin) { setTermsBlocking(false); return; }
        const accepted = me.terms_accepted_at && me.terms_version_accepted === currentTermsVersion;
        setTermsBlocking(!accepted);
    }, [data, currentTermsVersion, sessionData]);

    // iter35a — fetch co-admin context once we have a league + session.
    useEffect(() => {
        if (!leagueId) return;
        api.coAdminContext(leagueId).then(setCoAdminCtx).catch(() => setCoAdminCtx(null));
    }, [leagueId]);

    // iter87.199.3 — Admin-only WS connectivity pill. The "Live updates"
    // signal previously lived in the CHEER header on the public
    // dashboard. It's really an operator signal ("real-time feed is
    // healthy, everything will refresh automatically") so it belongs
    // next to the template badge on the admin scope banner. Same
    // lifecycle guard: hide once the tournament reaches final /
    // completed since nothing new is going to move.
    const [wsConnected, setWsConnected] = useState(false);
    useEffect(() => {
        if (!leagueId) return undefined;
        const sock = openLeagueSocket(leagueId, (msg) => {
            if (msg?.event === "connected") setWsConnected(true);
        });
        return () => {
            setWsConnected(false);
            sock.close();
        };
    }, [leagueId]);

    // iter86.90 — ESPN retirement: the sync-repaired broadcast
    // listener was removed with the site-admin Sync Repair card.

    const acceptTermsNow = async (version) => {
        if (!sessionData?.participant_id) return;
        setTermsAccepting(true);
        try {
            await api.acceptTerms(sessionData.participant_id, version || currentTermsVersion || "v1");
            setTermsBlocking(false);
            toast.success("Thanks — terms accepted");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't save acceptance");
        }
        setTermsAccepting(false);
    };

    // Split the "still loading" vs "loaded but empty" states so we don't
    // flash the "No tournament configured / Create one" screen on every
    // Admin entry while the initial fetch is in flight (data === null on
    // first mount; load() is async).
    if (!data) {
        return (
            <div
                className="min-h-screen flex flex-col items-center justify-center gap-3"
                data-testid="admin-loading-state"
            >
                <div className="w-8 h-8 rounded-full border-2 border-cyber/40 border-t-cyber animate-spin" />
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Loading admin…</p>
            </div>
        );
    }
    if (!data.tournament) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4">
                <p className="text-zinc-400">No tournament configured.</p>
                <Link to={`/league/${leagueId}/setup`} className="brutal-btn-primary">Create one</Link>
            </div>
        );
    }

    const { tournament } = data;
    const league = data.league;
    const isLocked = !!tournament.completion_authorized_by;
    // iter87.199.124 — `isLocked` now keys on `completion_authorized_by`
    // (backend `common.tournament_is_locked`), stamped ONLY by the two
    // manual completion actions. A stale/zombie process writing
    // `status="completed"` directly to Mongo can no longer flip this.
    // iter87.473 (user-reported BUG — MLB Playoffs scoring editable
    // mid-tournament) — `isLocked` above is completion-ONLY and says
    // nothing about whether the games have actually started, but the
    // Scoring tile's own copy promises a kickoff/tee-off/boost-start
    // lock. `tournament.scoring_locked` is the real signal (backend
    // `common.is_scoring_locked` — same one Trophy Tokens already used)
    // — OR'd with `isLocked` so a completed tournament is still caught
    // even if `scoring_locked` is somehow stale on an old cached fetch.
    const scoringLocked = isLocked || !!tournament.scoring_locked;
    // iter87.160 — Surface `lifecycle_state === "final"` distinctly so
    // the Danger Zone can render a "Tournament is final — click Mark
    // Complete to close" banner (matches the visual language of the
    // "This tournament is complete" reopen banner shown when locked).
    const isFinal = !isLocked && tournament.lifecycle_state === "final";
    // iter87.132 — League-scoped tabs (Players/Invites/League Settings) must
    // NOT lock just because the currently-selected tournament is completed
    // (Pro+ Rivalries hold many tournaments — the archived one shouldn't
    // freeze manage-crew UX). Lock only when the Rivalry itself is
    // archived OR closed (user-corrected, iter87.408): a closed rivalry
    // is read-only for admins too — grey the write controls out instead
    // of letting the write attempt 423 and bounce the whole page to the
    // /hiatus splash (that splash is for suspended/archived TENANTS, not
    // this lighter per-league state).
    const isLeagueLocked = !!(league?.archived_at || league?.closed_at);
    // Custom tournaments get full controls; global templates (WC, MM, Olympics, Football) only get Scoring + Settings.
    const isCustom = !tournament.template || tournament.template === "custom";
    // iter84.19 — Legacy ESPN-backed templates (`world_cup`, `world_cup_knockout`)
    // are unstable enough that league admins occasionally need to dive in and
    // hand-correct teams/schedule/sync. The iter84.16 simplification was too
    // aggressive for those — bring the full tab set back for legacy ESPN only.
    // The new API-Football templates (`world_cup_api`, `world_cup_ko_api`)
    // stay simplified because their sync is reliable enough that the platform
    // owns those concerns.
    const isLegacyEspnTemplate = tournament.template === "world_cup" || tournament.template === "world_cup_knockout";
    // iter84.57.12 — API-Football templates were originally simplified to
    // Scoring + Settings only because their sync runs platform-side. But the
    // iter84.57.x series added real admin controls to the Live Sync card
    // (Refresh scores, Sync diagnosis modal, per-stage coverage chip) that
    // are now stranded if the tab itself is hidden. Surface the Data Feed /
    // Live Sync tab for API-Football templates too, while keeping
    // Teams/Schedule/Bracket Status hidden (those auto-materialize and
    // shouldn't need hand-correction).
    const _API_FOOTBALL_TEMPLATES_INLINE = new Set(["world_cup_api", "world_cup_ko_api", "premier_league_api", "la_liga_api", "champions_league_api", "club_season_api"]);
    const isApiFootballTemplate = _API_FOOTBALL_TEMPLATES_INLINE.has(tournament.template);
    // iter86.31.1 — Golf templates: Scoring + Live Sync + Settings only
    // (structure auto-materializes from the Slash Golf feed).
    const isGolfTemplate = (tournament.format || "") === "golf";
    const needsFullTabs = isCustom || isLegacyEspnTemplate;
    const needsLiveSyncTabOnly = !needsFullTabs && isApiFootballTemplate;
    // iter86.90 — ESPN retirement: the _ESPN_CAPABLE_TEMPLATES heuristic
    // was removed with the Data Feed gate's espn conditions.
    const _API_FOOTBALL_TEMPLATES = new Set(["world_cup_api", "world_cup_ko_api", "premier_league_api", "la_liga_api", "champions_league_api", "club_season_api"]);
    const isApiFootball = _API_FOOTBALL_TEMPLATES.has(tournament.template);
    // iter83.5 — Prefer the explicit `data_source` field when present. Tournaments
    // created before iter83.5 don't have it, so we fall back to the legacy
    // heuristic (espn_sport set OR template in the capability lists) so the
    // Live Sync tab keeps showing for those.
    const explicitDataSource = tournament.data_source || null;
    // iter86.90 — ESPN retirement: the isEspn / ESPN-capable heuristics
    // were removed with the Data Feed gate's espn conditions.
    const isGotSport = explicitDataSource === "gotsport";
    // Manual-only custom tournaments hide the Live Sync tab entirely.
    const isManualOnly = explicitDataSource === "manual";
    const adminParticipant = data.participants?.find(p => p.is_admin);
    const adminIsPlayer = adminParticipant ? (adminParticipant.is_player !== false) : true;

    const handleMarkComplete = async () => {
        if (completeConfirmText !== "COMPLETE") {
            toast.error("Type COMPLETE to confirm");
            return;
        }
        setCompleting(true);
        try {
            await api.completeTournament(tournament.id);
            toast.success("Tournament locked. Read-only mode is on.");
            setCompleteDialogOpen(false);
            setCompleteConfirmText("");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to mark complete");
        }
        setCompleting(false);
    };

    // iter35a — when the caller is a co-admin, hide tabs they can't use.
    const isCoAdmin = coAdminCtx?.role === "co_admin";
    const perms = coAdminCtx?.permissions || {};
    // iter87.465.22 (user-directed — "default templates are not draft.
    // Fix correctly. No bedside again") — Single source of truth
    // (`isDraftEligible`) shared with StartDraftBanner.jsx and
    // HeroStrip.jsx so the three can never drift apart again. The old
    // blacklist silently missed MLS Playoffs (the reported recurring
    // bug) and would keep missing every future playoff/pot template
    // the same way. Golf Position Draft keeps its info-only tile
    // (server-side auto-draft at tee-off) via its own explicit check.
    const hasDraftAdminSurface =
        tournament?.template === "golf_position_draft_api" || isDraftEligible(tournament);
    // iter87.548 — Squares admin tab only shows for the 4 Squares
    // templates (config makes no sense for anything else).
    // iter87.554 — Squares no longer gets its own tab OR the generic
    // Scoring tab's content (neither applies — game type/weighting/
    // unclaimed-box handling moved into Trophy Tokens, which is the
    // one place for everything Squares now). The real game itself is
    // picked in the wizard at creation time.
    const isSquaresTemplate = (tournament?.template || "").includes("_squares_");

    // iter45 (M3) — Different tab sets per scope.
    // iter84.16 — Templated tournaments hide Teams / Schedule / Bracket
    // Status / Data Feed because the platform feed owns those. Full tabs
    // remain on custom AND on the legacy ESPN templates (iter84.19).
    const tournamentTabs = needsFullTabs ? [
        { k: "teams", label: "Teams", perm: "can_manage_custom_tiles" },
        { k: "schedule", label: "Schedule", perm: "can_manage_custom_tiles" },
        { k: "bracket_status", label: "Bracket Status", perm: "can_manage_custom_tiles" },
        ...(isSquaresTemplate ? [] : [{ k: "scoring", label: "Scoring", perm: "can_configure_scoring" }]),
        ...(hasDraftAdminSurface ? [{ k: "draft", label: "Draft", perm: "can_run_draft" }] : []),
        { k: "live_sync", label: "Live Sync", perm: "can_manage_live_sync" },
        { k: "trophy_tokens", label: "Trophy Tokens", perm: "can_configure_trophy_tokens" },
        { k: "settings", label: "Tournament Settings", perm: "never_co_admin" },
        { k: "danger_zone", label: "Danger Zone", perm: "never_co_admin" },
    ] : needsLiveSyncTabOnly ? [
        ...(isSquaresTemplate ? [] : [{ k: "scoring", label: "Scoring", perm: "can_configure_scoring" }]),
        ...(hasDraftAdminSurface ? [{ k: "draft", label: "Draft", perm: "can_run_draft" }] : []),
        { k: "live_sync", label: "Live Sync", perm: "can_manage_live_sync" },
        { k: "trophy_tokens", label: "Trophy Tokens", perm: "can_configure_trophy_tokens" },
        { k: "settings", label: "Tournament Settings", perm: "never_co_admin" },
        { k: "danger_zone", label: "Danger Zone", perm: "never_co_admin" },
    ] : isGolfTemplate ? [
        { k: "scoring", label: "Scoring", perm: "can_configure_scoring" },
        ...(hasDraftAdminSurface ? [{ k: "draft", label: "Draft", perm: "can_run_draft" }] : []),
        { k: "live_sync", label: "Live Sync", perm: "can_manage_live_sync" },
        { k: "trophy_tokens", label: "Trophy Tokens", perm: "can_configure_trophy_tokens" },
        { k: "settings", label: "Tournament Settings", perm: "never_co_admin" },
        { k: "danger_zone", label: "Danger Zone", perm: "never_co_admin" },
    ] : [
        ...(isSquaresTemplate ? [] : [{ k: "scoring", label: "Scoring", perm: "can_configure_scoring" }]),
        ...(hasDraftAdminSurface ? [{ k: "draft", label: "Draft", perm: "can_run_draft" }] : []),
        { k: "live_sync", label: "Live Sync", perm: "can_manage_live_sync" },
        { k: "trophy_tokens", label: "Trophy Tokens", perm: "can_configure_trophy_tokens" },
        { k: "settings", label: "Tournament Settings", perm: "never_co_admin" },
        { k: "danger_zone", label: "Danger Zone", perm: "never_co_admin" },
    ];
    const leagueTabs = [
        { k: "players", label: "Crewmates", perm: "can_remove_participants" },
        { k: "invites", label: "Invites", perm: "can_invite_players" },
        { k: "messages", label: "Messages", perm: "can_post_messages" },
        { k: "co_admins", label: "Co-Admins", perm: "never_co_admin" },
        { k: "settings", label: "Rivalry Settings", perm: "never_co_admin" },
    ];
    const allTabs = scope === "league" ? leagueTabs : tournamentTabs;
    // iter87.194 — Trophy Tokens is Pro+ only (same gate as ride_story
    // and rivalries_intel). Read the composite `pro_plus_ok` signal
    // from the league envelope (unions user-tier grant with league
    // effective_tier — matches the backend gate). Falls back to the
    // legacy `subscription_tier in {paid, comp}` check on older
    // deploys that haven't sent the new field yet. Backend still
    // hard-gates writes with a 402 regardless.
    const _lg = data?.league || league;
    const isProPlusLeague = _lg?.pro_plus_ok === true
        || ["paid", "comp"].includes(_lg?.subscription_tier);
    const gatedTabs = isProPlusLeague
        ? allTabs
        : allTabs.filter(t => t.k !== "trophy_tokens");
    const visibleTabs = isCoAdmin ? gatedTabs.filter(t => perms[t.perm]) : gatedTabs;
    const activeTabLabel = visibleTabs.find(t => t.k === tab)?.label || "";
    // iter87.17 — Draft-style icon header per admin tile: every admin
    // Takeover now inherits the same visual language as the pick/boost
    // modals (icon tile + title + tone-tinted ring + chevron-down close).
    const activeTabHead = ADMIN_HEAD[tab] || {};
    // iter86.98 — status-first tile sub-lines for the bento grid.
    const tileSub = (k) => ({
        teams: `${tournament.teams?.length || 0} teams`,
        schedule: "Fixtures & results",
        bracket_status: "Advance & mark rounds",
        scoring: "Points & bonuses",
        espn: isGolfTemplate ? "Slash Golf refresh" : "Feed & imports",
        live_sync: (() => {
            const t = tournament.template || "";
            if (t.endsWith("_api")) return "API feed · field map";
            if (tournament.format === "golf") return "Slash Golf · field map";
            if (tournament.feed_csv_enabled || tournament.gotsport_enabled) return "CSV feed · field map";
            return "Manual · read-only note";
        })(),
        settings: scope === "league" ? "Identity, spectator mode" : "Template, window, config",
        players: `${data.participants?.length || 0} crewmates`,
        invites: "By email · invite code",
        messages: "Broadcast to the crew",
        co_admins: `${(data.participants || []).filter((p) => p.is_co_admin && !p.is_admin).length} active`,
        danger_zone: isLocked ? "Locked · read-only" : "Lock, clear, complete",
        draft: (() => {
            const tid = tournament?.id;
            const hasDraftHere = !!(tid && league?.drafts_by_tournament?.[tid]);
            const seasonStarted = Object.values(tournament?.round_started || {}).some(Boolean);
            const golfR1Started = !!tournament?.golf?.r1_started;
            const playStarted = tournament?.template === "golf_position_draft_api" ? golfR1Started : seasonStarted;
            if (playStarted) return "Draft locked — first game kicked off";
            if (tournament?.template === "golf_position_draft_api") return "Auto-drafts at tee-off";
            if (hasDraftHere) return "Draft complete";
            return "Kick off the auto-draft";
        })(),
        trophy_tokens: (() => {
            if (tournament?.template?.includes("_squares_")) {
                const game = tournament?.squares_game;
                if (tournament?.squares_finalized_at) return "Finalized";
                if (tournament?.squares_pool_locked_at) return "Pool locked";
                if (game) return `${game.away_name} @ ${game.home_name}`;
                return "No game attached";
            }
            const cfg = tournament?.trophy_tokens_config;
            const finalized = !!tournament?.trophy_tokens_finalized_at;
            if (finalized) return "Awards finalized";
            if (cfg?.enabled) return `On · ${cfg.allocation_mode === "per_player" ? `${cfg.amount}/player` : `${cfg.amount} pot`}`;
            return "Off · winner-take-all";
        })(),
    }[k] || "");

    return (
        <div className="min-h-screen pb-12">
            <header className="glass border-b border-white/10 px-4 lg:px-12 py-3 flex items-center gap-3 sticky top-0 z-30">
                <Link to={`/league/${leagueId}`} className="brutal-btn-ghost p-2 shrink-0" data-testid="admin-back-btn">
                    <ArrowLeft className="w-4 h-4" />
                </Link>
                <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500">Tiny Crew Rivalry:</p>
                    <h1 className="font-display font-black text-lg lg:text-2xl leading-none truncate">{league.name}</h1>
                </div>
                {/* iter45b — Tournament switcher only meaningful in Tournament Admin scope.
                    Switching here picks which tournament is being administered. */}
                {scope === "tournament" && (
                    <div className="flex shrink min-w-0">
                        <TournamentSwitcher
                            leagueId={leagueId}
                            currentTid={tournament?.id}
                            isAdmin={true}
                            onChange={(tid) => {
                                setViewTournamentId(tid);
                                setStoredViewTournament(leagueId, tid === tournament?.id ? null : tid);
                            }}
                            onNewTournament={() => navigate(`/league/${leagueId}/setup`)}
                            refreshKey={switcherRefreshKey}
                        />
                    </div>
                )}
                {/* iter86.99 — One UI: floating glass menu (FAB mobile /
                    Menu pill desktop) absorbs Help & FAQ, invite copy,
                    scope switch and Back-to-Dashboard. */}
                <AdminFloatingMenu
                    leagueId={leagueId}
                    league={league}
                    tournament={tournament}
                    scope={scope}
                    onSetScope={setScope}
                />
            </header>

            <div className="px-4 lg:px-12 py-6 max-w-6xl mx-auto space-y-6">
                {/* iter87.27 — Tournament-locked banner now only renders
                    when the admin is editing a tournament (not the
                    rivalry). Tournament lock is a per-tournament fact —
                    surfacing it while editing rivalry-wide settings
                    (players, invites, messages, co-admins) was
                    misleading because the rivalry itself is never
                    "locked" and those settings remain editable. */}
                {isLocked && scope === "tournament" && (
                    <div
                        className="brutal-card p-4 bg-blaze text-white border-black flex items-start gap-3"
                        data-testid="tournament-locked-banner"
                    >
                        <Flag className="w-5 h-5 mt-0.5 shrink-0" />
                        <div className="space-y-1">
                            <p className="font-display font-extrabold text-base uppercase">
                                Tournament locked — read-only
                            </p>
                            <p className="text-xs leading-relaxed">
                                This tournament has been marked complete{tournament.completed_at ? ` on ${new Date(tournament.completed_at).toLocaleDateString()}` : ""}.
                                No further edits to scores, teams, matches, drafts, or scoring rules are allowed.
                            </p>
                        </div>
                    </div>
                )}

                {/* iter45b — Scope is now driven by URL `?scope=` set by the
                    AdminMenu in the dashboard/admin header. Banner reminds the
                    admin which scope they're in.
                    iter47h — Tournament name is LOUD when editing a specific
                    tournament so multi-tournament admins can't quietly edit the
                    wrong one. */}
                <div
                    className={`brutal-card-light p-3 flex items-center gap-3 flex-wrap ${scope === "tournament" ? "border-volt" : "border-cyber/50"}`}
                    data-testid="admin-scope-banner"
                >
                    <div className={`w-8 h-8 border-2 border-black shadow-brutal flex items-center justify-center shrink-0 ${scope === "tournament" ? "bg-volt" : "bg-cyber"}`}>
                        {scope === "tournament" ? <Trophy className="w-4 h-4 text-black" strokeWidth={2.5} /> : <Users className="w-4 h-4 text-black" strokeWidth={2.5} />}
                    </div>
                    <div className="flex-1 min-w-0 basis-48">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                            {scope === "tournament" ? "You're editing tournament" : "You're editing league"}
                        </p>
                        {scope === "tournament" ? (
                            <div className="flex items-center gap-2 min-w-0">
                                <p className="font-display font-extrabold text-lg leading-tight text-volt truncate" data-testid="admin-scope-tournament-name">
                                    {tournament?.name || "(no tournament)"}
                                </p>
                                {/* iter84.17 — Compact template badge replaces the
                                    larger format card so admins can still see which
                                    template is wired without the wall of text.
                                    iter87.199.3 — Rounded pill (was square edges)
                                    to match the app-wide pill vocabulary. */}
                                {!isCustom && tournament?.template && (
                                    <span
                                        data-testid="admin-template-badge"
                                        className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full border border-cyber/50 bg-cyber/10 text-cyber text-[10px] font-bold uppercase tracking-wider"
                                        title={`Template: ${tournament.template}`}
                                    >
                                        {({
                                            world_cup: "World Cup",
                                            world_cup_knockout: "World Cup KO",
                                            world_cup_api: "World Cup",
                                            world_cup_ko_api: "World Cup KO",
                                            march_madness: "March Madness",
                                            football_playoffs: "Football Playoffs",
                                        }[tournament.template]) || tournament.template.replace(/_/g, " ")}
                                    </span>
                                )}
                                {/* iter87.199.3 — Live updates WS pill (relocated
                                    from the public dashboard's CHEER header per
                                    operator direction — it's really an operator
                                    signal). Same lifecycle guard as before: hide
                                    when the tournament is at final / completed. */}
                                {wsConnected
                                    && !tournament?.completion_authorized_by
                                    && tournament?.lifecycle_state !== "final"
                                    && tournament?.lifecycle_state !== "completed" && (
                                    <span
                                        data-testid="ws-connected-pill"
                                        title="Real-time updates are connected — everything refreshes automatically."
                                        className="shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] leading-none font-bold uppercase tracking-wider border border-neon/60 text-neon bg-neon/10"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-neon animate-pulse" />
                                        Live updates
                                    </span>
                                )}
                            </div>
                        ) : (
                            <p className="font-display font-extrabold text-lg leading-tight text-cyber truncate">
                                {league.name}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        data-testid="admin-scope-swap-btn"
                        onClick={() => setScope(scope === "tournament" ? "league" : "tournament")}
                        className="brutal-btn-ghost text-xs py-2 shrink-0"
                    >
                        {/* iter84.16 — Templated tournaments expose a slimmer
                            surface (Scoring + Settings only) so we label the
                            scope-swap with "Tournament Settings" instead of
                            "Tournament Admin" to signal there's less to
                            configure. Custom tournaments keep the full label. */}
                        Switch to {scope === "tournament"
                            ? "Rivalry Admin"
                            : (isCustom ? "Tournament Admin" : "Tournament Settings")} →
                    </button>
                </div>

                {coAdminCtx?.role === "co_admin" && (
                    <div className="brutal-card-light p-3 border-volt/40 flex items-start gap-2 text-xs" data-testid="co-admin-banner">
                        <span className="text-volt font-bold uppercase tracking-widest text-[10px]">Co-admin view</span>
                        <span className="text-zinc-400 leading-relaxed">
                            You're managing this rivalry as a co-admin. Some tabs and controls are limited based on global Site Admin permissions and this rivalry's own admin. Contact your Site Admin or this rivalry's primary admin to adjust what co-admins can do.
                        </span>
                    </div>
                )}

                {/* iter84.16 — Templated tournament sync status strip.
                    Shows the most recent sync timestamp from whichever feed
                    is wired (API-Football / ESPN / GotSport). League admins
                    can see at a glance whether the platform feed is healthy
                    without needing a Data Feed tab. */}
                {!isCustom && scope === "tournament" && (() => {
                    const last = tournament?.api_football_last_synced_at
                        || tournament?.espn_last_synced_at
                        || tournament?.gotsport_last_synced_at;
                    if (!last) return null;
                    const minsAgo = Math.max(0, Math.round((Date.now() - new Date(last).getTime()) / 60000));
                    // iter87.199.188 — Was a flat 60 min. The backend
                    // poller (`api_football_poller.py`) intentionally
                    // only syncs a tournament every ~6h (`DARK_WINDOW_SEC`)
                    // when no kickoff is imminent — the vast majority of
                    // a season's calendar, pre-season especially. A 60
                    // min threshold falsely showed "Feed delayed —
                    // platform is investigating" for every tournament
                    // between dark-window passes, which is normal, not
                    // a real incident. 6.5h gives one wake-cycle buffer
                    // past the dark window before genuinely alarming.
                    const stale = minsAgo > 390;
                    return (
                        <div
                            data-testid="templated-feed-status-strip"
                            className={`text-[11px] flex items-center gap-2 px-3 py-1.5 border ${stale ? "bg-amber-500/10 border-amber-500/40 text-amber-300" : "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"}`}
                        >
                            <span className="font-bold uppercase tracking-wider text-[10px]">
                                {stale ? "Feed delayed" : "Feed live"}
                            </span>
                            <span className="text-zinc-400">
                                Last update {minsAgo === 0 ? "just now" : `${minsAgo} min ago`}
                                {stale ? " — platform is investigating." : ""}
                            </span>
                        </div>
                    );
                })()}

                {/* iter86.98 — One UI Phase 4: bento tile home replaces the
                    tab strip. Same tab defs, gating and testids; a tile tap
                    opens the area in a Takeover (full-screen on mobile,
                    centered panel on desktop). */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-bento-grid">
                    {visibleTabs.map((t) => {
                        const Icon = TILE_ICONS[t.k] || Settings;
                        return (
                            <div key={t.k} className="relative">
                                <button
                                    type="button"
                                    data-testid={`tab-${t.k}-btn`}
                                    onClick={() => setTab(t.k)}
                                    className="w-full rounded-2xl border border-white/[0.08] bg-[#121212] p-4 text-left min-h-[110px] flex flex-col justify-between gap-3 transition-all duration-150 hover:border-cyber/60 hover:bg-[#161616] active:scale-[0.97]"
                                >
                                    <span className="w-9 h-9 rounded-full bg-cyber/10 border border-cyber/40 flex items-center justify-center">
                                        <Icon className="w-4 h-4 text-cyber" />
                                    </span>
                                    <span>
                                        <span className="block font-display font-extrabold text-sm uppercase tracking-tight leading-tight">{t.label}</span>
                                        <span className="block text-[10px] text-zinc-500 mt-0.5">{tileSub(t.k)}</span>
                                    </span>
                                </button>
                                {/* iter87.199.18 — `?` help popover, top-right, above tile click surface. */}
                                <TileHelpPopover tileKey={t.k} scope={scope} />
                            </div>
                        );
                    })}
                </div>

                {/* iter84.17 — Large `global-template-banner` removed in favor
                    of a compact template badge next to the tournament name. The
                    sync instructions live inside the Live Sync tab itself, which
                    is where admins act on them. */}

                {/* iter86.98 — One UI Phase 4: tile content opens in a
                    Takeover — full-screen with back chevron on mobile,
                    centered wide panel on desktop. Same gating as before. */}
                <Takeover
                    open={tab !== null}
                    onClose={() => setTab(null)}
                    title={activeTabLabel}
                    testId="admin-takeover"
                    maxWidthClass="lg:max-w-5xl"
                    icon={activeTabHead.icon}
                    iconTone={activeTabHead.tone}
                >

                {tab === "schedule" && scope === "tournament" && needsFullTabs && (
                    <ScheduleTab
                        tournament={tournament}
                        league={data?.league}
                        leagueId={leagueId}
                        isLocked={isLocked}
                        coAdminCtx={coAdminCtx}
                        sessionData={sessionData}
                        onChanged={load}
                        onOpenComplete={() => setCompleteDialogOpen(true)}
                    />
                )}

                {tab === "matches" && scope === "tournament" && needsFullTabs && (
                    <MatchesTab
                        tournament={tournament}
                        data={data}
                        isLocked={isLocked}
                        onChanged={load}
                    />
                )}

                {/* iter84.57.22 — Teams content gating bug-fix. Previously
                    gated on `isCustom`, which left the tab BLANK on legacy
                    ESPN templates (world_cup, world_cup_knockout, march_madness)
                    even though the tab BUTTON renders (per iter84.19 admins
                    need to hand-correct ESPN rosters). Aligning with the
                    sibling Schedule/Bracket-Status gates which already use
                    `needsFullTabs`. TeamsTab reads `tournament.teams` directly,
                    so it works for both custom and templated tournaments. */}
                {tab === "teams" && scope === "tournament" && needsFullTabs && (
                    <TeamsTab
                        tournament={tournament}
                        league={league}
                        leagueId={leagueId}
                        isLocked={isLocked}
                        onChanged={load}
                    />
                )}

                {tab === "bracket_status" && scope === "tournament" && needsFullTabs && (
                    <div className="space-y-6" data-testid="bracket-status-tab">
                        {/* iter62 — Consolidated "Bracket Status" view: advance
                            teams to next round on top, then mark round-as-played
                            below. Both apply only to custom tournaments. */}
                        <AdvanceTeamsTab tournament={tournament} onChanged={load} />
                        <div className="border-t-2 border-[#333]" />
                        <MarkRoundsTab tournament={tournament} onChanged={load} />
                    </div>
                )}

                {tab === "scoring" && scope === "tournament" && (
                    <ScoringTab
                        tournament={tournament}
                        scoring={scoring}
                        setScoring={setScoring}
                        isLocked={scoringLocked}
                        onChanged={load}
                    />
                )}

                {tab === "draft" && scope === "tournament" && hasDraftAdminSurface && (
                    <DraftTab
                        leagueId={leagueId}
                        tournament={tournament}
                        league={league}
                    />
                )}

                {tab === "players" && scope === "league" && (
                    <PlayersTab
                        league={league}
                        leagueId={leagueId}
                        tournament={tournament}
                        data={data}
                        sessionData={sessionData}
                        isLocked={isLeagueLocked}
                        onChanged={load}
                    />
                )}

                {tab === "invites" && scope === "league" && (
                    <RivalryInvitesTab
                        league={league}
                        leagueId={leagueId}
                        isLocked={isLeagueLocked}
                        onChanged={load}
                    />
                )}

                {tab === "messages" && scope === "league" && (
                    <RivalryMessagesTab
                        league={league}
                        leagueId={leagueId}
                        tournament={tournament}
                        isLocked={isLeagueLocked}
                        onChanged={load}
                    />
                )}

                {tab === "co_admins" && scope === "league" && (
                    <RivalryCoAdminsTab
                        league={league}
                        data={data}
                        isLocked={isLeagueLocked}
                        me={(data?.participants || []).find((p) => p.id === sessionData?.participant_id) || null}
                    />
                )}

                {tab === "settings" && scope === "league" && (
                    <LeagueSettingsTab
                        league={league}
                        leagueId={leagueId}
                        data={data}
                        coAdminCtx={coAdminCtx}
                        isLocked={isLeagueLocked}
                        onChanged={load}
                    />
                )}

                {tab === "settings" && scope === "tournament" && (
                    <TournamentSettingsTab
                        league={league}
                        leagueId={leagueId}
                        tournament={tournament}
                        isLocked={isLocked}
                        adminIsPlayer={adminIsPlayer}
                        participants={data?.participants || []}
                        onChanged={load}
                        coAdminCtx={coAdminCtx}
                        onOpenComplete={() => setCompleteDialogOpen(true)}
                    />
                )}

                {tab === "espn" && scope === "tournament" && isGolfTemplate && (
                    <GolfSyncCard tournament={tournament} />
                )}
                {tab === "espn" && scope === "tournament" && !isGolfTemplate && needsFullTabs && (
                    /* iter86.31.5 — ESPN retired; only custom/legacy CSV
                       templates still expose the hands-on Data Feed tab.
                       iter87.7 — Superseded by Live Sync; kept for backward
                       compat until we're sure no bookmark still points here. */
                    <EspnSyncTab
                        tournament={tournament}
                        league={league}
                        leagueId={leagueId}
                        isLocked={isLocked}
                        isCustom={isCustom}
                        onChanged={load}
                    />
                )}

                {/* iter87.7 — Unified Live Sync tile: every tournament
                    template renders the same "what's powering scores?"
                    surface (API + last sync + read-only field map) with
                    actionable feed operations mounted below for the
                    templates that expose them. */}
                {tab === "live_sync" && scope === "tournament" && (
                    <LiveSyncTab
                        league={league}
                        leagueId={leagueId}
                        tournament={tournament}
                        isLocked={isLocked}
                        onChanged={load}
                    />
                )}

                {/* iter87.9 — Danger Zone plane. Every one-way / destructive
                    tournament action lives here so admins never fat-finger
                    a wipe from a routine settings visit. */}
                {tab === "danger_zone" && scope === "tournament" && (
                    <TournamentDangerZoneCard
                        league={league}
                        leagueId={leagueId}
                        tournament={tournament}
                        isLocked={isLocked}
                        isFinal={isFinal}
                        onOpenComplete={() => setCompleteDialogOpen(true)}
                        onChanged={load}
                    />
                )}

                {/* iter87.194 — Trophy Tokens: per-tournament admin token
                    system. Config, roster, preview, and finalize all live
                    in one Takeover. iter87.554 — for Squares templates,
                    this tab shows Squares' config/roster/lock/finalize
                    instead (early return inside TrophyTokensTab). */}
                {tab === "trophy_tokens" && scope === "tournament" && (
                    <TrophyTokensTab
                        tournament={tournament}
                        league={league}
                        onChanged={load}
                    />
                )}
                </Takeover>
            </div>

            {/* Mark Complete confirmation dialog (type "COMPLETE" to confirm) */}
            {completeDialogOpen && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 px-4"
                    data-testid="complete-confirm-dialog"
                    onClick={(e) => { if (e.target === e.currentTarget) { setCompleteDialogOpen(false); setCompleteConfirmText(""); } }}
                >
                    <div className="brutal-card p-6 max-w-md w-full bg-ink border-black space-y-4">
                        <div className="flex items-start gap-3">
                            <Flag className="w-6 h-6 text-blaze shrink-0 mt-1" />
                            <div>
                                <h3 className="font-display font-black text-xl uppercase">Lock this tournament?</h3>
                                <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
                                    All edits (scores, teams, advancement, drafts, scoring rules, ESPN sync) will be blocked. {isProPlusLeague
                                        ? "This rivalry is Pro+, so the completed tournament stays available — no auto-archive countdown."
                                        : "Since this is a free rivalry, this also closes it for everyone and starts the 30-day countdown to archive."} Only site support can reopen it after this — there's no self-serve undo.
                                </p>
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">
                                Type <span className="text-blaze">COMPLETE</span> to confirm
                            </label>
                            <input
                                data-testid="complete-confirm-input"
                                type="text"
                                value={completeConfirmText}
                                onChange={(e) => setCompleteConfirmText(e.target.value)}
                                className="brutal-input w-full"
                                placeholder="COMPLETE"
                                autoFocus
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                data-testid="complete-cancel-btn"
                                onClick={() => { setCompleteDialogOpen(false); setCompleteConfirmText(""); }}
                                className="brutal-btn-ghost text-xs"
                                disabled={completing}
                            >
                                Cancel
                            </button>
                            <button
                                data-testid="complete-confirm-btn"
                                onClick={handleMarkComplete}
                                disabled={completing || completeConfirmText !== "COMPLETE"}
                                className="brutal-btn text-xs bg-blaze text-white border-black shadow-brutal disabled:opacity-40"
                            >
                                {completing ? "Locking..." : "Lock tournament"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* iter31n — blocking T&C modal for admins who haven't accepted yet. */}
            <TermsModal
                open={termsBlocking}
                onAccept={acceptTermsNow}
                onClose={null}
                busy={termsAccepting}
                title="Welcome — please accept our terms"
                onShowPrivacy={() => setPrivacyModalOpen(true)}
            />
            <PrivacyModal open={privacyModalOpen} onClose={() => setPrivacyModalOpen(false)} />
        </div>
    );
}
